import { useState, useEffect } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { confirmacionesAPI } from "@/lib/api";
import { ArrowLeft, Edit, Printer, Flame, Calendar, MapPin, User, Users, Droplets } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";

export default function ConfirmacionDetailPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [confirmacion, setConfirmacion] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadConfirmacion();
  }, [id]);

  const loadConfirmacion = async () => {
    try {
      const response = await confirmacionesAPI.getOne(id);
      setConfirmacion(response.data);
    } catch (error) {
      toast.error("Error al cargar la confirmación");
      navigate("/confirmaciones");
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-stone-400">Cargando...</div>
      </div>
    );
  }

  if (!confirmacion) return null;

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="confirmacion-detail-page">
      {/* Header - No print */}
      <div className="flex items-center justify-between no-print">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/confirmaciones")}
            className="h-10 w-10"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-3xl font-serif text-rose-950 tracking-tight flex items-center gap-3">
              <Flame className="w-8 h-8 text-amber-600" strokeWidth={1.5} />
              Certificado de Confirmación
            </h1>
            <p className="text-stone-600 mt-1">Partida #{confirmacion.numero_partida}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Link to={`/confirmaciones/${id}/editar`}>
            <Button variant="outline" className="h-10">
              <Edit className="w-4 h-4 mr-2" />
              Editar
            </Button>
          </Link>
          <Button onClick={handlePrint} className="h-10 bg-rose-950 hover:bg-rose-900 text-white">
            <Printer className="w-4 h-4 mr-2" />
            Imprimir Certificado
          </Button>
        </div>
      </div>

      {/* Print Certificate */}
      <div className="print-certificate">
        {/* Print Header */}
        <div className="print-header hidden print:block text-center mb-8">
          <h1 className="text-3xl font-serif text-rose-950">CERTIFICADO DE CONFIRMACIÓN</h1>
          <p className="text-stone-600 mt-2">Parroquia San Juan Bautista</p>
          <p className="text-stone-500 text-sm">Diócesis de San Juan</p>
        </div>

        {/* Datos del Registro */}
        <Card className="border-stone-200 bg-white mb-6">
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-medium text-stone-800">
                Datos del Registro
              </CardTitle>
              <Badge variant="outline" className="text-amber-600 border-amber-200 bg-amber-50">
                Libro {confirmacion.numero_libro} · Folio {confirmacion.numero_folio} · Partida {confirmacion.numero_partida}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="flex items-start gap-3">
              <Calendar className="w-5 h-5 text-stone-400 mt-0.5" />
              <div>
                <p className="text-xs text-stone-500 uppercase tracking-wide">Fecha</p>
                <p className="text-stone-800 font-medium">{confirmacion.fecha_confirmacion}</p>
              </div>
            </div>
            <div className="flex items-start gap-3 md:col-span-3">
              <MapPin className="w-5 h-5 text-stone-400 mt-0.5" />
              <div>
                <p className="text-xs text-stone-500 uppercase tracking-wide">Lugar</p>
                <p className="text-stone-800 font-medium">{confirmacion.lugar_confirmacion}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Datos del Confirmado */}
        <Card className="border-stone-200 bg-white mb-6">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg font-medium text-stone-800 flex items-center gap-2">
              <User className="w-5 h-5 text-amber-600" />
              Confirmado
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <p className="text-xs text-stone-500 uppercase tracking-wide">Nombre Completo</p>
              <p className="text-xl font-serif text-stone-800 mt-1">
                {confirmacion.confirmado_nombre} {confirmacion.confirmado_apellido}
              </p>
            </div>
            <div>
              <p className="text-xs text-stone-500 uppercase tracking-wide">Género</p>
              <p className="text-stone-800 mt-1 capitalize">{confirmacion.confirmado_genero}</p>
            </div>
            <div>
              <p className="text-xs text-stone-500 uppercase tracking-wide">Fecha de Nacimiento</p>
              <p className="text-stone-800 mt-1">{confirmacion.confirmado_fecha_nacimiento}</p>
            </div>
            <div>
              <p className="text-xs text-stone-500 uppercase tracking-wide">Lugar de Nacimiento</p>
              <p className="text-stone-800 mt-1">{confirmacion.confirmado_lugar_nacimiento}</p>
            </div>
          </CardContent>
        </Card>

        {/* Datos del Bautizo */}
        {(confirmacion.parroquia_bautizo || confirmacion.fecha_bautizo) && (
          <Card className="border-stone-200 bg-white mb-6">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg font-medium text-stone-800 flex items-center gap-2">
                <Droplets className="w-5 h-5 text-blue-600" />
                Datos del Bautizo
              </CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <p className="text-xs text-stone-500 uppercase tracking-wide">Parroquia</p>
                <p className="text-stone-800 mt-1">{confirmacion.parroquia_bautizo || "—"}</p>
              </div>
              <div>
                <p className="text-xs text-stone-500 uppercase tracking-wide">Fecha</p>
                <p className="text-stone-800 mt-1">{confirmacion.fecha_bautizo || "—"}</p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Padres y Padrinos */}
        <Card className="border-stone-200 bg-white mb-6">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg font-medium text-stone-800 flex items-center gap-2">
              <Users className="w-5 h-5 text-emerald-600" />
              Padres y Padrinos
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <p className="text-xs text-stone-500 uppercase tracking-wide">Padre</p>
              <p className="text-stone-800 mt-1">{confirmacion.padre_nombre || "—"}</p>
            </div>
            <div>
              <p className="text-xs text-stone-500 uppercase tracking-wide">Madre</p>
              <p className="text-stone-800 mt-1">{confirmacion.madre_nombre || "—"}</p>
            </div>
            <Separator className="md:col-span-2" />
            <div>
              <p className="text-xs text-stone-500 uppercase tracking-wide">Padrino</p>
              <p className="text-stone-800 mt-1">{confirmacion.padrino_nombre || "—"}</p>
            </div>
            <div>
              <p className="text-xs text-stone-500 uppercase tracking-wide">Madrina</p>
              <p className="text-stone-800 mt-1">{confirmacion.madrina_nombre || "—"}</p>
            </div>
          </CardContent>
        </Card>

        {/* Obispo */}
        <Card className="border-stone-200 bg-white mb-6">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg font-medium text-stone-800">
              Obispo Celebrante
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-stone-800 font-medium">{confirmacion.obispo_nombre}</p>
          </CardContent>
        </Card>

        {/* Notas */}
        {confirmacion.notas_marginales && (
          <Card className="border-stone-200 bg-white">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg font-medium text-stone-800">
                Notas Marginales
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-stone-600 whitespace-pre-wrap">{confirmacion.notas_marginales}</p>
            </CardContent>
          </Card>
        )}

        {/* Print Signature Area */}
        <div className="print-signature hidden print:flex justify-between mt-16 pt-8">
          <div className="text-center">
            <div className="w-48 border-t border-stone-400 pt-2">
              <p className="text-sm text-stone-600">Firma del Párroco</p>
            </div>
          </div>
          <div className="text-center">
            <div className="w-48 border-t border-stone-400 pt-2">
              <p className="text-sm text-stone-600">Sello Parroquial</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
